import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useStore, useStores, useSyncStore, useUpdateStore, useDeleteStore } from '../hooks/useStores.js';
import { usePosByStore, useCreatePos, useUpdatePos, useSyncPos, useDeletePos } from '../hooks/usePos.js';
import { useTerminals, useCreateTerminal, useAssignTerminal } from '../hooks/useTerminals.js';
import { useState, useEffect } from 'react';
import Table from '../components/Table.jsx';
import Modal from '../components/Modal.jsx';
import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';
import Badge from '../components/Badge.jsx';
import Spinner from '../components/Spinner.jsx';
import AddressSearch from '../components/AddressSearch.jsx';
import LocationFields from '../components/LocationFields.jsx';

export default function StoreDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: store, isLoading } = useStore(id);
  const syncMutation = useSyncStore(id);
  const updateMutation = useUpdateStore(id);
  const deleteMutation = useDeleteStore(id);
  const { data: posList, isLoading: posLoading } = usePosByStore(id);
  const createPosMutation = useCreatePos(id);
  const updatePosMutation = useUpdatePos(id);
  const syncPosMutation = useSyncPos(id);
  const deletePosMutation = useDeletePos(id);
  const { data: allTerminals } = useTerminals();
  const { data: allStores } = useStores();
  const createTerminalMutation = useCreateTerminal();

  const [posOpen, setPosOpen] = useState(false);
  const [posForm, setPosForm] = useState({ externalId: '', name: '', fixedAmount: true });
  const [posErr, setPosErr] = useState(null);

  const [editPosOpen, setEditPosOpen] = useState(false);
  const [editPosData, setEditPosData] = useState(null);
  const [editPosErr, setEditPosErr] = useState(null);
  const [syncMsg, setSyncMsg] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editErr, setEditErr] = useState(null);

  const [termForm, setTermForm] = useState({ mpTerminalId: '', operatingMode: 'PDV' });
  const [termErr, setTermErr] = useState(null);

  const [deletePosErr, setDeletePosErr] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteErr, setDeleteErr] = useState(null);

  // Auto-abrir edición de un POS específico cuando se navega desde el mapa (nodo amarillo)
  useEffect(() => {
    const autoId = location.state?.autoOpenPosId;
    if (!autoId || !posList?.length || editPosOpen) return;
    const pos = posList.find(p => p.id === autoId);
    if (pos) {
      setEditPosData({ id: pos.id, name: pos.name, category: pos.category || '', mp_pos_id: pos.mp_pos_id, external_id: pos.external_id, fixedAmount: pos.fixed_amount ?? true });
      setEditPosOpen(true);
      setEditPosErr(null);
    }
  }, [posList, location.state?.autoOpenPosId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-abrir creación de POS con prefill cuando viene de un POS fantasma del mapa
  useEffect(() => {
    if (!location.state?.autoOpenPos || !location.state?.prefillPos) return;
    const pf = location.state.prefillPos;
    setPosForm(f => ({
      ...f,
      externalId:    pf.externalId || '',
      name:          pf.name || '',
      mpPosIdToLink: pf.mpPosIdToLink || null,  // pre-vincular al MP POS si viene del mapa
    }));
    setPosOpen(true);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return <div className="flex justify-center py-10"><Spinner size="lg" /></div>;
  if (!store) return <div className="card text-slate-400">Store no encontrada</div>;

  const openEdit = () => {
    setEditForm({
      externalId:   store.external_id || '',
      name:         store.name || '',
      description:  store.description || '',
      streetName:   store.street_name || '',
      streetNumber: store.street_number || '',
      zipCode:      store.zip_code || '',
      cityName:     store.city_name || '',
      stateName:    store.state_name || '',
      latitude:     store.latitude?.toString() || '',
      longitude:    store.longitude?.toString() || '',
      mpStoreId:    store.mp_store_id || '',
    });
    setEditErr(null);
    setEditOpen(true);
  };

  const handleUpdate = () => {
    setEditErr(null);
    if (!editForm.name?.trim()) return setEditErr('El nombre es obligatorio');

    const payload = {
      ...editForm,
      latitude:  editForm.latitude  ? parseFloat(editForm.latitude)  : undefined,
      longitude: editForm.longitude ? parseFloat(editForm.longitude) : undefined,
    };
    delete payload.mpStoreId;
    // externalId solo se envía si la store no está sincronizada
    if (store.mp_store_id) delete payload.externalId;

    updateMutation.mutate(payload, {
      onSuccess: () => { setEditOpen(false); setSyncMsg(null); },
      onError: (e) => {
        const d = e.response?.data;
        setEditErr(Array.isArray(d?.error)
          ? d.error.map(i => i.message).join(' · ')
          : d?.message || d?.error || 'Error al actualizar');
      },
    });
  };

  const f = (k) => (e) => setEditForm(p => ({ ...p, [k]: e.target.value }));
  const handleLocationChange = (field, value) => setEditForm(p => ({ ...p, [field]: value }));

  const handleSync = () => {
    setSyncMsg(null);
    syncMutation.mutate(undefined, {
      onSuccess: (d) => setSyncMsg(`Sincronizado: MP Store ID ${d.mp_store_id}`),
      onError: (e) => setSyncMsg(`Error: ${e.response?.data?.error || e.response?.data?.message || 'fallo sync'}`),
    });
  };

  const handleCreatePos = () => {
    setPosErr(null);
    if (!posForm.externalId?.trim()) {
      setPosErr('El ID Externo es requerido (solo mayúsculas y números, sin espacios)');
      return;
    }
    // Eliminar campos UI-only antes de enviar al API
    const { mpList, mpListLoading, importPreview, ...posData } = posForm;
    // mpPosIdToLink se incluye en posData → el backend lo detecta y vincula sin crear en MP
    createPosMutation.mutate(posData, {
      onSuccess: () => {
        setPosOpen(false);
        setPosForm({ externalId: '', name: '', fixedAmount: true });
      },
      onError: (e) => setPosErr(e.response?.data?.message || e.response?.data?.error || 'Error al crear POS'),
    });
  };

  const handleUpdatePos = () => {
    setEditPosErr(null);
    if (!editPosData?.name?.trim()) return setEditPosErr('El nombre es obligatorio');
    const payload = {
      posId: editPosData.id,
      name: editPosData.name,
      category: editPosData.category || undefined,
      fixedAmount: editPosData.fixedAmount,
      ...(editPosData.targetStoreId ? { storeId: editPosData.targetStoreId } : {}),
    };
    updatePosMutation.mutate(payload, {
      onSuccess: () => {
        setEditPosOpen(false);
        setEditPosData(null);
        if (editPosData.targetStoreId) {
          // El POS se movió a otra store — navegar allá
          navigate(`/stores/${editPosData.targetStoreId}`);
        }
      },
      onError: (e) => setEditPosErr(e.response?.data?.error || 'Error al actualizar'),
    });
  };

  const posColumns = [
    { key: 'external_id', header: 'ID Externo' },
    { key: 'name', header: 'Nombre' },
    { key: 'mp_pos_id', header: 'MP POS ID', render: (r) => r.mp_pos_id || <span className="text-slate-500">—</span> },
    {
      key: 'terminal', header: 'Terminal',
      render: (r) => {
        const term = (allTerminals || []).find(t => t.pos_id === r.id);
        return term
          ? <span className="font-mono text-xs text-emerald-400">✓ {term.mp_terminal_id.split('__')[1] || term.mp_terminal_id}</span>
          : <span className="text-xs text-amber-400">Sin terminal</span>;
      },
    },
    { key: 'status', header: 'Estado', render: (r) => <Badge variant={r.status === 'active' ? 'success' : 'danger'}>{r.status}</Badge> },
    {
      key: 'actions', header: '',
      render: (r) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); setEditPosData({ id: r.id, name: r.name, category: r.category || '', mp_pos_id: r.mp_pos_id, external_id: r.external_id, fixedAmount: r.fixed_amount ?? true, targetStoreId: undefined }); setEditPosOpen(true); setEditPosErr(null); }}
            className="text-xs text-accent hover:text-accent-hover"
          >
            ✏ Editar
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (!confirm(`¿Eliminar el POS "${r.name}" (${r.external_id})?\n\nEl POS se eliminará de MercadoPago y de tu sistema.`)) return;
              setDeletePosErr(null);
              deletePosMutation.mutate(r.id, {
                onError: (e) => {
                  const d = e.response?.data;
                  const detail = d?.mp_error?.errors?.[0]?.message ?? d?.mp_error?.message ?? null;
                  const base = d?.message || d?.error || 'Error al eliminar el POS';
                  setDeletePosErr(base + (detail ? ` — ${detail}` : ''));
                },
              });
            }}
            className="text-xs text-red-400 hover:text-red-300 whitespace-nowrap"
          >
            Eliminar
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/stores" className="text-slate-400 hover:text-white text-sm">← Stores</Link>
        <Badge variant={store.status === 'active' ? 'success' : 'danger'}>{store.status}</Badge>
        {store.mp_store_id ? <Badge variant="info">MP ✓ {store.mp_store_id}</Badge> : <Badge variant="warning">Sin sync MP</Badge>}
      </div>

      <div className="card space-y-3">
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-semibold">{store.name}</h2>
          <Button variant="ghost" className="text-xs" onClick={openEdit}>✏ Editar</Button>
        </div>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <div><dt className="text-slate-400">ID Externo</dt><dd className="font-mono">{store.external_id}</dd></div>
          <div><dt className="text-slate-400">Ciudad</dt><dd>{store.city_name}{store.state_name ? `, ${store.state_name}` : ''}</dd></div>
          {(store.street_name || store.street_number) && (
            <div><dt className="text-slate-400">Dirección</dt><dd>{store.street_name} {store.street_number}</dd></div>
          )}
          {store.latitude && (
            <div><dt className="text-slate-400">Coordenadas</dt><dd className="font-mono text-xs">{store.latitude}, {store.longitude}</dd></div>
          )}
          {store.description && (
            <div className="col-span-2"><dt className="text-slate-400">Descripción</dt><dd>{store.description}</dd></div>
          )}
        </dl>
        {!store.street_name && (
          <p className="text-xs text-amber-400">⚠ Dirección incompleta — completala para poder sincronizar con MP</p>
        )}
      </div>

      <div className="flex gap-3 flex-wrap">
        {!store.mp_store_id && (
          <Button onClick={handleSync} loading={syncMutation.isPending} variant="secondary">
            Sincronizar con MP
          </Button>
        )}
        <Button onClick={() => setPosOpen(true)}>+ Nuevo POS</Button>
        <Button variant="danger" onClick={() => { setDeleteErr(null); setDeleteOpen(true); }}>
          Eliminar sucursal
        </Button>
      </div>

      {syncMsg && (
        <p className={`text-sm ${syncMsg.startsWith('Error') ? 'text-red-400' : 'text-emerald-400'}`}>
          {syncMsg}
        </p>
      )}

      <div>
        <h3 className="font-medium mb-3">POS / Cajas</h3>
        <Table columns={posColumns} data={posList} loading={posLoading} empty="No hay POS en esta store" />
        {deletePosErr && (
          <p className="mt-2 text-sm text-red-400 bg-red-900/20 border border-red-800 rounded px-3 py-2">
            {deletePosErr}
          </p>
        )}
      </div>

      {/* Modal editar store */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Editar store" maxWidth="max-w-2xl"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button loading={updateMutation.isPending} onClick={handleUpdate}>Guardar</Button>
          </>
        }
      >
        {/* ID Externo — editable solo si no está sincronizado con MP */}
        {!store.mp_store_id ? (
          <div>
            <Input label="ID Externo *" value={editForm.externalId || ''} onChange={f('externalId')} placeholder="SOU001" />
            <p className="text-xs text-slate-500 mt-1">Código único de la sucursal. Solo editable antes de sincronizar con MP.</p>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-400">ID Externo:</span>
            <span className="font-mono">{store.external_id}</span>
            <span className="text-xs text-slate-500">(no editable: ya sincronizado con MP)</span>
          </div>
        )}
        <Input label="Nombre *" value={editForm.name || ''} onChange={f('name')} />
        <AddressSearch
          lat={editForm.latitude}
          lon={editForm.longitude}
          onSelect={(fields) => setEditForm(p => ({ ...p, ...fields }))}
        />
        <hr className="border-border my-1" />
        <LocationFields form={editForm} onChange={handleLocationChange} showCountry={false} />
        <Input label="Descripción" value={editForm.description || ''} onChange={f('description')} />

        {/* Vincular MP Store ID existente */}
        <div className="p-3 bg-bg rounded-md border border-border space-y-2">
          <p className="text-xs font-medium text-slate-300">Vincular con store existente en MP</p>
          <p className="text-xs text-slate-500">
            Si la sucursal ya existe en MercadoPago, ingresá su ID para vincularla sin crear una nueva.
            Podés obtenerlo desde el listado de terminales de MP (<code className="text-slate-400">store_id</code> en cada dispositivo).
          </p>
          <div className="flex gap-2">
            <input
              className="input-base text-sm font-mono flex-1"
              placeholder="ej: 72360153"
              value={editForm.mpStoreId || ''}
              onChange={(e) => setEditForm(p => ({ ...p, mpStoreId: e.target.value }))}
            />
            <Button
              variant="secondary"
              className="text-xs shrink-0"
              loading={updateMutation.isPending}
              disabled={!editForm.mpStoreId?.trim()}
              onClick={async () => {
                setEditErr(null);
                try {
                  const { apiPost } = await import('../api/client.js');
                  await apiPost(`/stores/${id}/link-mp`, { mpStoreId: editForm.mpStoreId.trim() });
                  setEditOpen(false);
                  setSyncMsg(`Vinculado: MP Store ID ${editForm.mpStoreId.trim()}`);
                  window.location.reload();
                } catch (e) {
                  setEditErr(e.response?.data?.error || 'Error al vincular');
                }
              }}
            >
              Vincular
            </Button>
          </div>
          {store.mp_store_id && (
            <p className="text-xs text-emerald-400">Actualmente vinculado: {store.mp_store_id}</p>
          )}
        </div>

        {editErr && <p className="text-sm text-red-400">{editErr}</p>}
      </Modal>

      {/* Modal editar POS */}
      <Modal open={editPosOpen} onClose={() => setEditPosOpen(false)} title="Editar POS"
        footer={<><Button variant="ghost" onClick={() => setEditPosOpen(false)}>Cancelar</Button><Button loading={updatePosMutation.isPending} onClick={handleUpdatePos}>Guardar nombre/categoría</Button></>}
      >
        {/* Estado actual */}
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-bg border border-border">
            <span className="text-slate-500">ID Externo:</span>
            <span className="font-mono text-slate-200 font-medium">{editPosData?.external_id || <span className="text-slate-500 italic">sin ID externo</span>}</span>
          </div>
          {editPosData?.mp_pos_id
            ? <Badge variant="success">MP ✓ {editPosData.mp_pos_id}</Badge>
            : <Badge variant="warning">Sin sync MP</Badge>}
        </div>

        {/* Campos editables */}
        <Input label="Nombre *" value={editPosData?.name || ''} onChange={(e) => setEditPosData(p => ({ ...p, name: e.target.value }))} />
        <Input label="Categoría (MCC)" value={editPosData?.category || ''} onChange={(e) => setEditPosData(p => ({ ...p, category: e.target.value }))} placeholder="ej: 5411" />

        {/* Fixed amount toggle */}
        <label className="flex items-center gap-3 p-3 bg-bg-elevated rounded-md border border-border cursor-pointer">
          <div className="relative shrink-0">
            <input type="checkbox" className="sr-only" checked={!!editPosData?.fixedAmount}
              onChange={(e) => setEditPosData(p => ({ ...p, fixedAmount: e.target.checked }))} />
            <div style={{ width:36,height:20,borderRadius:10,background: editPosData?.fixedAmount ? '#00b1ea':'#475569',transition:'background .2s',position:'relative' }}>
              <div style={{ width:16,height:16,borderRadius:'50%',background:'white',position:'absolute',top:2,left:editPosData?.fixedAmount?18:2,transition:'left .2s' }} />
            </div>
          </div>
          <div>
            <p className="text-sm text-slate-200">Monto fijo <span className="font-mono text-xs text-slate-400">(fixed_amount)</span></p>
            <p className="text-xs text-slate-500">{editPosData?.fixedAmount ? 'El importe lo determina la orden del sistema (PDV)' : 'El operador puede ingresar el importe en la terminal'}</p>
          </div>
        </label>

        {/* Mover a otra sucursal */}
        {allStores && allStores.filter(s => s.id !== id).length > 0 && (
          <div className="p-3 bg-bg rounded-md border border-border space-y-2">
            <p className="text-xs font-medium text-slate-300">Mover a otra sucursal</p>
            <select
              className="w-full px-3 py-2 rounded-lg text-sm border border-border bg-bg-elevated"
              style={{ color: 'var(--text-primary)' }}
              value={editPosData?.targetStoreId || ''}
              onChange={(e) => setEditPosData(p => ({ ...p, targetStoreId: e.target.value || undefined }))}
            >
              <option value="">— Mantener en {store?.name} —</option>
              {(allStores || []).filter(s => s.id !== id).map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {editPosData?.targetStoreId && (
              <p className="text-xs text-amber-400">⚠ Al guardar, el POS se trasladará a la nueva sucursal y serás redirigido allá.</p>
            )}
          </div>
        )}

        {/* Sección MP — siempre visible */}
        <div className="p-3 bg-bg rounded-md border border-border space-y-3">
          <p className="text-xs font-medium text-slate-300">MercadoPago</p>

          {/* Sync automático (solo si sin mp_pos_id) */}
          {!editPosData?.mp_pos_id && store.mp_store_id && (
            <Button variant="secondary" className="text-xs w-full" loading={syncPosMutation.isPending}
              onClick={() => {
                setEditPosErr(null);
                syncPosMutation.mutate(editPosData?.id, {
                  onSuccess: (d) => { setEditPosOpen(false); setSyncMsg(`POS sincronizado: MP POS ID ${d.mp_pos_id}`); },
                  onError: (e) => setEditPosErr(e.response?.data?.error || 'Error al sincronizar'),
                });
              }}
            >
              Crear en MP y sincronizar
            </Button>
          )}
          {!editPosData?.mp_pos_id && !store.mp_store_id && (
            <p className="text-xs text-amber-400">⚠ La store no está sincronizada con MP. Sincronizá la store primero.</p>
          )}

          {/* Ver POS en MP */}
          <div>
            <p className="text-xs text-slate-400 mb-1.5">
              {editPosData?.mp_pos_id ? 'Actualizar datos o cambiar el POS vinculado:' : 'Seleccioná un POS existente de MP:'}
            </p>
            <Button variant="secondary" className="text-xs w-full"
              loading={editPosData?.mpListLoading}
              onClick={async () => {
                setEditPosErr(null);
                setEditPosData(p => ({ ...p, mpList: null, mpLookup: null, mpListLoading: true }));
                try {
                  const { apiGet } = await import('../api/client.js');
                  const data = await apiGet(`/stores/${id}/pos/mp-lookup`);
                  const mpPosList = data.pos || [];
                  // Auto-match: buscar el POS de MP con el mismo external_id que el local
                  const extId = editPosData?.external_id;
                  const autoMatch = extId
                    ? mpPosList.find(mp => mp.external_id === extId)
                    : null;
                  setEditPosData(p => ({
                    ...p,
                    mpList: mpPosList,
                    mpListLoading: false,
                    mpAutoMatched: !!autoMatch,
                    // Si hay match automático por external_id, pre-seleccionarlo como mpLookup
                    mpLookup: autoMatch ?? null,
                    ...(autoMatch ? { name: autoMatch.name || p.name, category: autoMatch.category || p.category, fixedAmount: autoMatch.fixed_amount ?? p.fixedAmount } : {}),
                  }));
                } catch (e) {
                  setEditPosData(p => ({ ...p, mpListLoading: false }));
                  setEditPosErr(e.response?.data?.error || 'Error al listar POS de MP');
                }
              }}
            >
              Ver POS en MP →
            </Button>

            {/* Lista de POS de MP */}
            {editPosData?.mpList && (
              <div className="mt-2 space-y-1">
                {editPosData.mpList.length === 0 && (
                  <p className="text-xs text-slate-500">No hay POS en esta store en MP</p>
                )}
                {editPosData.mpList.map(mp => (
                  <button key={mp.mp_pos_id}
                    className={`w-full text-left p-2 rounded border text-xs transition-all ${
                      editPosData.mpLookup?.mp_pos_id === mp.mp_pos_id
                        ? 'border-accent bg-accent/10'
                        : 'border-border hover:border-border-strong bg-bg-elevated'
                    }`}
                    onClick={() => setEditPosData(p => ({
                      ...p,
                      mpLookup: mp,
                      name: mp.name || p.name,
                      category: mp.category || p.category,
                      fixedAmount: mp.fixed_amount ?? p.fixedAmount,
                    }))}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{mp.name}</span>
                      <span className="font-mono text-slate-400">{mp.mp_pos_id}</span>
                    </div>
                    {mp.external_id && <div className="text-slate-500">ext: {mp.external_id}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Preview datos de MP */}
          {editPosData?.mpLookup && (
            <div className="p-2 bg-bg-elevated rounded border border-emerald-700/40 space-y-2">
              <p className="text-xs font-medium text-emerald-300">
                ✓ Encontrado en MP
                {editPosData?.mpAutoMatched && (
                  <span className="ml-2 text-emerald-500 font-normal">— auto-match por ID Externo</span>
                )}
              </p>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                {[
                  ['MP POS ID',    editPosData.mpLookup.mp_pos_id],
                  ['Nombre en MP', editPosData.mpLookup.name],
                  ['External ID',  editPosData.mpLookup.external_id || '—'],
                  ['Estado',       editPosData.mpLookup.status],
                  ['Fixed amount', editPosData.mpLookup.fixed_amount ? 'Sí' : 'No'],
                  ['Site',         editPosData.mpLookup.mp_raw?.site],
                  ['User ID',      editPosData.mpLookup.mp_raw?.user_id],
                  ['Store ID MP',  editPosData.mpLookup.store_id],
                ].filter(([,v]) => v).map(([label, val]) => (
                  <div key={label}><dt className="text-slate-500">{label}</dt><dd className="font-mono break-all">{val}</dd></div>
                ))}
              </dl>
              {editPosData.mpLookup.mp_raw?.qr?.image && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Código QR del POS:</p>
                  <img src={editPosData.mpLookup.mp_raw.qr.image} alt="QR POS"
                    className="h-24 w-24 rounded border border-border bg-white p-1" />
                </div>
              )}
              <p className="text-xs text-slate-400">Nombre y fixed_amount pre-llenados en el form. "Guardar" actualiza tu BD.</p>
              <Button variant="primary" className="text-xs w-full"
                onClick={async () => {
                  setEditPosErr(null);
                  try {
                    const { apiPost } = await import('../api/client.js');
                    await apiPost(`/stores/${id}/pos/${editPosData.id}/link-mp`, { mpPosId: editPosData.mpLookup.mp_pos_id });
                    setEditPosOpen(false);
                    setSyncMsg(`POS vinculado: MP POS ID ${editPosData.mpLookup.mp_pos_id}`);
                    window.location.reload();
                  } catch (e) {
                    setEditPosErr(e.response?.data?.error || 'Error al vincular');
                  }
                }}
              >
                Vincular al MP POS ID {editPosData.mpLookup.mp_pos_id}
              </Button>
            </div>
          )}
        </div>

        {/* ── Terminales ── */}
        <div className="p-3 bg-bg rounded-md border border-border space-y-3">
          <p className="text-xs font-medium text-slate-300">Terminales (dispositivos)</p>

          {/* Terminales ya asignadas a este POS */}
          {(() => {
            const linked = (allTerminals || []).filter(t => t.pos_id === editPosData?.id);
            if (!linked.length) return <p className="text-xs text-slate-500">No hay terminales vinculadas a este POS.</p>;
            return (
              <div className="space-y-1">
                {linked.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-2 bg-bg-elevated rounded border border-border text-xs">
                    <div>
                      <span className="font-mono text-slate-200">{t.mp_terminal_id}</span>
                      {t.operating_mode && <span className="ml-2 text-slate-400">{t.operating_mode}</span>}
                    </div>
                    <Badge variant={t.status === 'active' ? 'success' : 'danger'}>{t.status}</Badge>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Form para agregar nueva terminal */}
          <div className="space-y-2 pt-1 border-t border-border">
            <p className="text-xs text-slate-400">Agregar terminal al POS:</p>
            <input
              className="input-base text-sm font-mono"
              placeholder="NEWLAND_N950__XXXXXXXXX"
              value={termForm.mpTerminalId}
              onChange={e => setTermForm(p => ({ ...p, mpTerminalId: e.target.value.trim() }))}
            />
            <div className="flex gap-2 items-center">
              <select
                className="input-base text-sm flex-1"
                value={termForm.operatingMode}
                onChange={e => setTermForm(p => ({ ...p, operatingMode: e.target.value }))}
              >
                <option value="PDV">PDV (monto fijo por orden)</option>
                <option value="STANDALONE">STANDALONE (monto manual)</option>
              </select>
              <Button
                variant="secondary"
                className="text-xs shrink-0"
                loading={createTerminalMutation.isPending}
                disabled={!termForm.mpTerminalId || !editPosData?.id}
                onClick={() => {
                  setTermErr(null);
                  createTerminalMutation.mutate(
                    { mpTerminalId: termForm.mpTerminalId, posId: editPosData.id, operatingMode: termForm.operatingMode },
                    {
                      onSuccess: () => setTermForm({ mpTerminalId: '', operatingMode: 'PDV' }),
                      onError: (e) => setTermErr(e.response?.data?.error || e.response?.data?.message || 'Error al crear terminal'),
                    }
                  );
                }}
              >
                + Agregar
              </Button>
            </div>
            {termErr && <p className="text-xs text-red-400">{termErr}</p>}
          </div>
        </div>

        {editPosErr && <p className="text-sm text-red-400">{editPosErr}</p>}
      </Modal>

      {/* Modal eliminar sucursal */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Eliminar sucursal"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>Cancelar</Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() => {
                setDeleteErr(null);
                deleteMutation.mutate(undefined, {
                  onSuccess: () => navigate('/stores'),
                  onError: (e) => {
                    const d = e.response?.data;
                    setDeleteErr(d?.message || 'Error al eliminar la sucursal');
                  },
                });
              }}
            >
              Eliminar
            </Button>
          </>
        }
      >
        {(posList?.length > 0) ? (
          <div className="space-y-3">
            <div className="p-3 rounded-lg border border-amber-600/50 bg-amber-900/20 text-sm text-amber-300">
              ⚠ Esta sucursal tiene <strong>{posList.length} POS asignado{posList.length !== 1 ? 's' : ''}</strong>:
              <ul className="mt-2 space-y-0.5 text-xs text-amber-400">
                {posList.map(p => <li key={p.id}>• {p.name} ({p.external_id})</li>)}
              </ul>
            </div>
            <p className="text-sm text-slate-300">
              Eliminá primero todos los POS desde esta pantalla y luego podrás borrar la sucursal.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-300">
              ¿Eliminar la sucursal <strong>{store.name}</strong>?
            </p>
            <p className="text-xs text-slate-400">
              La sucursal se eliminará de tu sistema.
              {store.mp_store_id && ' El registro en MercadoPago no se elimina automáticamente.'}
            </p>
          </div>
        )}
        {deleteErr && <p className="text-sm text-red-400 mt-2">{deleteErr}</p>}
      </Modal>

      {/* Modal nuevo POS */}
      <Modal open={posOpen} onClose={() => setPosOpen(false)} title="Nuevo POS"
        footer={<><Button variant="ghost" onClick={() => setPosOpen(false)}>Cancelar</Button><Button loading={createPosMutation.isPending} onClick={handleCreatePos}>Crear</Button></>}
      >
        {/* Importar desde MP */}
        {store.mp_store_id && (
          <div className="p-3 bg-bg rounded border border-border space-y-2">
            <p className="text-xs text-slate-400">¿Ya existe en MP? Seleccionalo para pre-llenar:</p>
            <Button variant="ghost" className="text-xs w-full"
              loading={posForm.mpListLoading}
              onClick={async () => {
                try {
                  const { apiGet } = await import('../api/client.js');
                  const d = await apiGet(`/stores/${id}/pos/mp-lookup`);
                  setPosForm(p => ({ ...p, mpList: d.pos || [], mpListLoading: false }));
                } catch (e) { setPosErr(e.response?.data?.error || 'Error al listar'); }
              }}
            >
              Ver POS en MP →
            </Button>
            {posForm.mpList?.map(mp => (
              <button key={mp.mp_pos_id}
                className={`w-full text-left p-2 rounded border text-xs transition-all ${
                  posForm.mpPosIdToLink === mp.mp_pos_id ? 'border-accent bg-accent/10' : 'border-border hover:border-border-strong bg-bg-elevated'
                }`}
                onClick={() => setPosForm(p => ({
                  ...p,
                  // Si el POS de MP no tiene external_id propio, usar el mp_pos_id como fallback (único garantizado)
                  externalId: mp.external_id || p.externalId || String(mp.mp_pos_id),
                  name: mp.name || p.name,
                  mpPosIdToLink: mp.mp_pos_id,
                  importPreview: mp,
                }))}
              >
                <span className="font-medium">{mp.name}</span>
                <span className="font-mono text-slate-400 ml-2">{mp.mp_pos_id}</span>
                {mp.external_id && <span className="text-slate-500 ml-2">ext: {mp.external_id}</span>}
              </button>
            ))}
            {posForm.importPreview && (
              <p className="text-xs text-emerald-400">✓ Seleccionado: {posForm.importPreview.name} — campos pre-llenados</p>
            )}
          </div>
        )}
        <hr className="border-border" />
        <div>
          <Input label="ID Externo *" value={posForm.externalId} onChange={(e) => setPosForm(p => ({ ...p, externalId: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))} placeholder="SUC001POS001" />
          <p className="text-xs text-slate-500 mt-1">Solo mayúsculas y números, sin guiones ni espacios. MP lo requiere así.</p>
        </div>
        <Input label="Nombre *" value={posForm.name} onChange={(e) => setPosForm(p => ({ ...p, name: e.target.value }))} placeholder="Caja 1" />

        <label className="flex items-start gap-3 p-3 bg-bg-elevated rounded-md border border-border cursor-pointer select-none">
          <div className="relative mt-0.5 shrink-0">
            <input type="checkbox" className="sr-only" checked={posForm.fixedAmount}
              onChange={(e) => setPosForm(p => ({ ...p, fixedAmount: e.target.checked }))} />
            <div style={{ width: 36, height: 20, borderRadius: 10, background: posForm.fixedAmount ? '#00b1ea' : '#475569', transition: 'background 0.2s', position: 'relative' }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'white', position: 'absolute', top: 2, left: posForm.fixedAmount ? 18 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-slate-200">
              Monto fijo <span className="font-mono text-xs text-slate-400">(fixed_amount)</span>
              {posForm.fixedAmount
                ? <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300 border border-emerald-700/50">ON</span>
                : <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">OFF</span>}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {posForm.fixedAmount
                ? 'El importe lo determina la orden del sistema. Recomendado para integración PDV.'
                : 'El operador puede ingresar el importe en la terminal.'}
            </p>
          </div>
        </label>

        {posErr && <p className="text-sm text-red-400">{posErr}</p>}
        {!store.mp_store_id && <p className="text-xs text-amber-400">⚠ La store no está sincronizada con MP. El POS se creará localmente.</p>}
      </Modal>
    </div>
  );
}
